import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react"
import {
  GoogleMap,
  useLoadScript,
  DirectionsRenderer,
} from "@react-google-maps/api"
import MilesIcon from "@/public/icons/miles.svg"
import { useRouter } from "next/router"
import { useTheme } from "@/lib/context/ThemeContext"
import Image from "next/image"
import { CubeIcon } from "@heroicons/react/24/outline"

const formatDeliveryDate = (value?: string) => {
  if (!value || value === "-") return "–"

  const parsedDate = new Date(value)
  return Number.isNaN(parsedDate.getTime()) ? "–" : parsedDate.toDateString()
}

type Props = {
  directionsResponse: (google.maps.DirectionsResult & { selectedRouteIndex?: number }) | null | any
  milesData?: { miles: string; date: string }
  deliveryDate?: Date
  isEmpty?: boolean
  isRouting?: boolean
  date: Date;
  setDate: Dispatch<SetStateAction<Date>>;
}

export const DeliveryRoute: FC<Props> = (props) => {
  const { pathname } = useRouter()
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_API_GOOGLE_MAPS_KEY as string,
  })
  const [center, setCenter] = useState({
    lat: 41.87,
    lng: -87.45,
  })
  const zoom = 8
  const [map, setMap] = useState<google.maps.Map | null>(null)

  const { isDarkMode } = useTheme()

  const options = useMemo(
    () => ({
      disableDefaultUI: true,
      ClickableIcons: false,
      styles:
        isDarkMode === true
          ? [
            {
              elementType: "geometry",
              stylers: [
                {
                  color: "#242f3e",
                },
              ],
            },
            {
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#746855",
                },
              ],
            },
            {
              elementType: "labels.text.stroke",
              stylers: [
                {
                  color: "#242f3e",
                },
              ],
            },
            {
              featureType: "administrative.locality",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#d59563",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#d59563",
                },
              ],
            },
            {
              featureType: "poi.park",
              elementType: "geometry",
              stylers: [
                {
                  color: "#263c3f",
                },
              ],
            },
            {
              featureType: "poi.park",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#6b9a76",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "geometry",
              stylers: [
                {
                  color: "#38414e",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#212a37",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#9ca5b3",
                },
              ],
            },
            {
              featureType: "road.arterial",
              elementType: "labels",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "geometry",
              stylers: [
                {
                  color: "#746855",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#1f2835",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "labels",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#f3d19c",
                },
              ],
            },
            {
              featureType: "road.local",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "transit",
              elementType: "geometry",
              stylers: [
                {
                  color: "#2f3948",
                },
              ],
            },
            {
              featureType: "transit.station",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#d59563",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "geometry",
              stylers: [
                {
                  color: "#17263c",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#515c6d",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "labels.text.stroke",
              stylers: [
                {
                  color: "#17263c",
                },
              ],
            },
          ]
          : [],
    }),
    [isDarkMode],
  )

  const [renderDirections, setRenderDirections] = useState(false)
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0)
  const directionsRenderKey = useMemo(() => {
    const route = props.directionsResponse?.routes?.[selectedRouteIndex] || props.directionsResponse?.routes?.[0]
    if (!route?.legs?.length) return `empty-${selectedRouteIndex}`

    const firstLeg = route.legs[0]
    const lastLeg = route.legs[route.legs.length - 1]

    return [
      selectedRouteIndex,
      firstLeg?.start_location?.toUrlValue?.() ?? "",
      lastLeg?.end_location?.toUrlValue?.() ?? "",
      route.legs.length,
    ].join("|")
  }, [props.directionsResponse, selectedRouteIndex])

  // US bounds for route validation
  const US_BOUNDS = {
    north: 49.0,
    south: 24.396308,
    west: -125.0,
    east: -66.93457,
  }

  const routeStaysInUS = (route: google.maps.DirectionsRoute) => {
    if (!route?.overview_path) return false
    return route.overview_path.every((p) => {
      const lat = p.lat()
      const lng = p.lng()
      return (
        lat >= US_BOUNDS.south &&
        lat <= US_BOUNDS.north &&
        lng >= US_BOUNDS.west &&
        lng <= US_BOUNDS.east
      )
    })
  }

  const formatDistance = (distance: google.maps.Distance | null | undefined) => {
    if (!distance) return "N/A"
    return distance.text
  }

  const formatDuration = (duration: google.maps.Duration | null | undefined) => {
    if (!duration) return "N/A"
    return duration.text
  }

  // Parse miles from string like "1234 Miles" or "1 Mile"
  const parseMilesFromString = (milesString: string): number | null => {
    if (!milesString || milesString === "-") return null
    // Extract number from strings like "1234 Miles", "1 Mile", "1234.5 Miles"
    const match = milesString.match(/([\d.]+)/)
    return match ? parseFloat(match[1]) : null
  }

  useEffect(() => {
    if (props.directionsResponse?.selectedRouteIndex !== undefined) {
      setSelectedRouteIndex(props.directionsResponse.selectedRouteIndex)
    }
  }, [props.directionsResponse?.selectedRouteIndex])

  // Match route distance to displayed miles
  useEffect(() => {
    if (!props.milesData?.miles || !props.directionsResponse?.routes) return

    const apiMiles = parseMilesFromString(props.milesData.miles)
    if (!apiMiles || apiMiles <= 0) return

    const routes = props.directionsResponse.routes
    if (routes.length === 0) return

    let closestRouteIndex = 0
    let minDifference = Infinity

    routes.forEach((route: google.maps.DirectionsRoute, index: number) => {
      // Calculate total distance in meters
      const totalMeters = route.legs.reduce(
        (sum: number, leg: google.maps.DirectionsLeg) => sum + (leg.distance?.value || 0),
        0,
      )

      // Convert to miles
      const routeMiles = totalMeters / 1609.34

      // Calculate difference
      const difference = Math.abs(routeMiles - apiMiles)

      if (difference < minDifference) {
        minDifference = difference
        closestRouteIndex = index
      }
    })

    // Only update if we found a reasonable match (within 10% tolerance)
    const routeMiles =
      routes[closestRouteIndex].legs.reduce(
        (sum: number, leg: google.maps.DirectionsLeg) => sum + (leg.distance?.value || 0),
        0,
      ) / 1609.34
    const tolerance = apiMiles * 0.1 // 10% tolerance

    if (minDifference <= tolerance) {
      setSelectedRouteIndex(closestRouteIndex)
    }
  }, [props.milesData?.miles, props.directionsResponse?.routes])

  useEffect(() => {
    if (!props.directionsResponse?.routes?.length) {
      setRenderDirections(false)
    }
  }, [props.directionsResponse])

  useEffect(() => {
    if (isLoaded && props.directionsResponse != null && map != null) {
      const directions = props.directionsResponse
      if (directions.routes && directions.routes.length > 0) {
        const selectedRoute = directions.routes[selectedRouteIndex] || directions.routes[0]
        const legs = selectedRoute.legs
        const locations = legs.flatMap(
          (leg: {
            start_location: google.maps.LatLng
            end_location: google.maps.LatLng
          }) => [leg.start_location, leg.end_location],
        )
        const latitudes = locations.map((location: { lat: () => number }) =>
          location.lat(),
        )
        const longitudes = locations.map((location: { lng: () => number }) =>
          location.lng(),
        )
        const averageLng =
          (Math.max(...longitudes) + Math.min(...longitudes)) / 2
        const averageLat = (Math.max(...latitudes) + Math.min(...latitudes)) / 2
        const newCenter = {
          lat: averageLat,
          lng: averageLng,
        }
        setCenter(newCenter)
        const bounds = new window.google.maps.LatLngBounds()
        locations.forEach((location: google.maps.LatLng) =>
          bounds.extend(location),
        )
        map.fitBounds(bounds)
        setMap(map)

        setRenderDirections(false)
        setTimeout(() => setRenderDirections(true), 0)
      }
    }
  }, [isLoaded, map, props.directionsResponse, selectedRouteIndex])

  return (
    <div className="col-span-1 flex flex-col justify-between max-h-full lg:mt-0 mt-6 w-full shadow-custom p-4 rounded-xl border dark:border-secondary-7 border-primary-10">
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3 2xl:gap-4">
        {/* Primary: section title — match Select Equipment for consistency */}
        <h1 className="dark:text-primary-10 text-light-2 2xl:text-28 text-2xl mb-0 shrink-0">
          Shipment Route
        </h1>
        {/* Secondary: metadata */}
        {props.milesData && props.milesData.miles != "" && (
          <div className="flex flex-wrap items-center gap-3 text-sm font-normal text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-white/10">
                <Image
                  className="h-4 w-4 opacity-60 dark:opacity-50"
                  src={MilesIcon.src}
                  width={100}
                  height={100}
                  alt="Distance"
                />
              </div>
              <span>{props.milesData.miles}</span>
            </div>
            <span className="text-gray-400 dark:text-gray-500" aria-hidden="true">•</span>
            <div className="flex items-center gap-2">
              <CubeIcon className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />
              <span>
                Delivery {formatDeliveryDate(props.milesData?.date)}
              </span>
            </div>
          </div>
        )}
      </div>
      {/* DO NOT REMOVE THIS */}
      {/* {props.directionsResponse?.routes && props.directionsResponse.routes.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {props.directionsResponse.routes.map((route: google.maps.DirectionsRoute, index: number) => {
            const totalDistance = route.legs.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0)
            const totalDuration = route.legs.reduce((sum, leg) => sum + (leg.duration?.value || 0), 0)
            const isUS = routeStaysInUS(route)
            const isSelected = index === selectedRouteIndex

            return (
              <button
                key={index}
                onClick={() => setSelectedRouteIndex(index)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isSelected
                    ? "bg-primary-3 dark:bg-primary-10 text-white dark:text-primary-1"
                    : "bg-gray-200 dark:bg-secondary-13 text-gray-700 dark:text-primary-8 hover:bg-gray-300 dark:hover:bg-secondary-12"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>Route {index + 1}</span>
                  {isUS ? (
                    <span className="text-xs">🇺🇸</span>
                  ) : (
                    <span className="text-xs">🌎</span>
                  )}
                  <span className="text-xs opacity-75">
                    {formatDistance(route.legs[0]?.distance)} • {formatDuration(route.legs[0]?.duration)}
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      )} */}
      {isLoaded && (
        <div className="relative w-full h-full">
          <GoogleMap
            center={center}
            zoom={zoom}
            options={options}
            onLoad={(map) => {
              setMap(map)
            }}
            mapContainerClassName="w-full h-full object-cover rounded-lg"
            mapContainerStyle={{
              width: "100%",
              height: "100%",
              position: "relative",
            }}
          >
            <div className="flex w-full h-80">
              {renderDirections && props.directionsResponse && (
                <DirectionsRenderer
                  key={directionsRenderKey}
                  directions={{
                    ...props.directionsResponse,
                    routes: [props.directionsResponse.routes[selectedRouteIndex] || props.directionsResponse.routes[0]],
                  }}
                  options={{
                    preserveViewport: true,
                  }}
                />
              )}
            </div>
          </GoogleMap>
          {props.isRouting && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-black/50 rounded-lg text-sm text-gray-700 dark:text-gray-200">
              Calculating route...
            </div>
          )}
        </div>
      )}
    </div>
  )
}
